import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  FormControl,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  InputLabel,
  Select,
  FormHelperText,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  Fade,
  Grow,
  Slide,
  useMediaQuery,
  useTheme,
  Tab,
  Tabs,
  Pagination,
  Tooltip,
} from "@mui/material";
import {
  BugReport,
  PriorityHigh,
  CheckCircle,
  Cancel,
  Pending,
  Assignment,
  Person,
  Description,
  Send,
  Clear,
  Refresh,
  History,
  Visibility,
  Update,
  Delete,
  Close,
  SupportAgent,
  AttachMoney,
  Help,
  Info,
  Warning,
  Error as ErrorIcon,
  CancelOutlined,
  AccessTime,
  CalendarToday,
  AccountCircle,
  Star,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import { collection, addDoc, getDocs, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import frLocale from "date-fns/locale/fr";
import { db, auth } from '../Firebase/Config';

// Priority levels
const priorities = [
  { value: "low", label: "Faible", color: "#10b981", icon: <TrendingDown /> },
  { value: "medium", label: "Moyenne", color: "#f59e0b", icon: <TrendingUp /> },
  { value: "high", label: "Haute", color: "#ef4444", icon: <PriorityHigh /> },
  { value: "critical", label: "Critique", color: "#dc2626", icon: <ErrorIcon /> },
];

// Ticket categories
const categories = [
  { value: "bug", label: "Bug / Erreur", icon: <BugReport /> },
  { value: "feature", label: "Demande de fonctionnalité", icon: <Star /> },
  { value: "improvement", label: "Amélioration", icon: <TrendingUp /> },
  { value: "support", label: "Support technique", icon: <SupportAgent /> },
  { value: "billing", label: "Facturation", icon: <AttachMoney /> },
  { value: "other", label: "Autre", icon: <Help /> },
];

// Ticket statuses
const statuses = [
  { value: "new", label: "Nouveau", color: "#3b82f6", icon: <Assignment /> },
  { value: "in_progress", label: "En cours", color: "#f59e0b", icon: <Pending /> },
  { value: "resolved", label: "Résolu", color: "#10b981", icon: <CheckCircle /> },
  { value: "closed", label: "Fermé", color: "#6b7280", icon: <Cancel /> },
  { value: "rejected", label: "Rejeté", color: "#ef4444", icon: <CancelOutlined /> },
];

const StyledSnackbar = ({ open, onClose, message, severity }) => {
  const icons = {
    success: <CheckCircle sx={{ fontSize: 20 }} />,
    error: <ErrorIcon sx={{ fontSize: 20 }} />,
    warning: <Warning sx={{ fontSize: 20 }} />,
    info: <Info sx={{ fontSize: 20 }} />
  };

  const colors = {
    success: "#10b981",
    error: "#ef4444",
    warning: "#f59e0b",
    info: "#3b82f6"
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={4000}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "left" }}
    >
      <Paper
        elevation={6}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          minWidth: 320,
          maxWidth: 400,
          borderRadius: 2,
          borderLeft: `4px solid ${colors[severity]}`,
          background: "white",
          boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ 
          width: 40, 
          height: 40, 
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${colors[severity]}20`,
          color: colors[severity]
        }}>
          {icons[severity]}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {severity === "success" ? "Succès" : 
             severity === "error" ? "Erreur" : 
             severity === "warning" ? "Attention" : "Information"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};

const ReclamationPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    priority: "medium",
    description: "",
    stepsToReproduce: "",
    expectedBehavior: "",
    actualBehavior: "",
    attachments: [],
    systemInfo: {
      browser: "",
      os: "",
      screenResolution: "",
      userAgent: ""
    }
  });

  const [formErrors, setFormErrors] = useState({});
  const restaurantId = localStorage.getItem("restaurantId");

  // Get current user from Firebase Auth
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email?.split('@')[0] || "Utilisateur",
          photoURL: user.photoURL
        });
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load tickets from Firebase (simple get all)
  useEffect(() => {
    if (currentUser) {
      loadTickets();
    }
  }, [currentUser]);

  useEffect(() => {
    filterTickets();
  }, [tickets, filterStatus, filterPriority, searchTerm]);

  // Collect system info on mount
  useEffect(() => {
    const systemInfo = {
      browser: navigator.userAgent,
      os: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      userAgent: navigator.userAgent
    };
    setFormData(prev => ({ ...prev, systemInfo }));
  }, []);

  const loadTickets = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const ticketsRef = collection(db, "tickets");
      const querySnapshot = await getDocs(ticketsRef);
      
      const ticketsData = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
          updatedAt: doc.data().updatedAt?.toDate?.() || new Date(doc.data().updatedAt)
        }))
        .filter(ticket => ticket.userId === currentUser.uid)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setTickets(ticketsData);
    } catch (error) {
      console.error("Error loading tickets:", error);
      showSnackbar("Erreur lors du chargement des tickets", "error");
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = [...tickets];
    
    if (filterStatus !== "all") {
      filtered = filtered.filter(t => t.status === filterStatus);
    }
    
    if (filterPriority !== "all") {
      filtered = filtered.filter(t => t.priority === filterPriority);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredTickets(filtered);
    setPage(1);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) errors.title = "Le titre est requis";
    if (!formData.category) errors.category = "La catégorie est requise";
    if (!formData.description.trim()) errors.description = "La description est requise";
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      showSnackbar("Vous devez être connecté pour créer un ticket", "warning");
      navigate("/login");
      return;
    }
    
    if (!validateForm()) {
      showSnackbar("Veuillez remplir tous les champs obligatoires", "error");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const ticketData = {
        ...formData,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName,
        restaurantId: restaurantId,
        status: "new",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.email,
      };
      
      await addDoc(collection(db, "tickets"), ticketData);
      
      showSnackbar("Ticket créé avec succès !", "success");
      
      // Reset form
      setFormData({
        title: "",
        category: "",
        priority: "medium",
        description: "",
        stepsToReproduce: "",
        expectedBehavior: "",
        actualBehavior: "",
        attachments: [],
        systemInfo: formData.systemInfo
      });
      
      // Reload tickets
      await loadTickets();
      
    } catch (error) {
      console.error("Error creating ticket:", error);
      showSnackbar("Erreur lors de la création du ticket", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const viewTicketDetails = (ticket) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedTicket(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (date) => {
    if (!date) return "";
    try {
      return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: frLocale });
    } catch {
      return "";
    }
  };

  const getPriorityInfo = (priority) => {
    return priorities.find(p => p.value === priority) || priorities[1];
  };

  const getStatusInfo = (status) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  const getCategoryInfo = (category) => {
    return categories.find(c => c.value === category) || categories[0];
  };

  const paginatedTickets = filteredTickets.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  if (!currentUser) {
    return (
      <Box sx={{ 
        p: 4, 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        bgcolor: "#f3f4f6"
      }}>
        <Paper sx={{ p: 4, textAlign: "center", maxWidth: 400, borderRadius: 3 }}>
          <AccountCircle sx={{ fontSize: 60, color: "#9ca3af", mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Connexion requise
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Veuillez vous connecter pour créer et consulter vos tickets
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{ borderRadius: 2 }}
          >
            Se connecter
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      bgcolor: "#f3f4f6",
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          color: "white"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Avatar sx={{ bgcolor: "#3b82f6", width: 56, height: 56 }}>
            <BugReport sx={{ fontSize: 32 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Support & Réclamations
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Créez et suivez vos tickets de support
            </Typography>
          </Box>
          <Chip
            icon={<Person />}
            label={currentUser.displayName || currentUser.email}
            sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }}
          />
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<BugReport />} label="Nouveau Ticket" />
          <Tab icon={<History />} label="Mes Tickets" />
        </Tabs>
      </Paper>

      {/* Content */}
      {tabValue === 0 ? (
        <Fade in>
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <BugReport color="primary" />
              Créer un nouveau ticket
            </Typography>
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Titre du ticket *"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    error={!!formErrors.title}
                    helperText={formErrors.title}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Assignment color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.category}>
                    <InputLabel>Catégorie *</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      label="Catégorie *"
                    >
                      {categories.map(cat => (
                        <MenuItem key={cat.value} value={cat.value}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {cat.icon}
                            {cat.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.category && <FormHelperText>{formErrors.category}</FormHelperText>}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priorité</InputLabel>
                    <Select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      label="Priorité"
                    >
                      {priorities.map(p => (
                        <MenuItem key={p.value} value={p.value}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {p.icon}
                            <span style={{ color: p.color }}>{p.label}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description détaillée *"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    error={!!formErrors.description}
                    helperText={formErrors.description}
                    multiline
                    rows={4}
                    placeholder="Décrivez le problème de manière détaillée..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Étapes pour reproduire"
                    name="stepsToReproduce"
                    value={formData.stepsToReproduce}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    placeholder="1. ...\n2. ...\n3. ..."
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Comportement attendu"
                    name="expectedBehavior"
                    value={formData.expectedBehavior}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Comportement actuel"
                    name="actualBehavior"
                    value={formData.actualBehavior}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Informations système (collectées automatiquement)
                    </Typography>
                    <Typography variant="caption" display="block">
                      Navigateur: {formData.systemInfo.browser?.substring(0, 100)}...
                    </Typography>
                    <Typography variant="caption" display="block">
                      OS: {formData.systemInfo.os}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Résolution: {formData.systemInfo.screenResolution}
                    </Typography>
                  </Alert>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFormData({
                          title: "",
                          category: "",
                          priority: "medium",
                          description: "",
                          stepsToReproduce: "",
                          expectedBehavior: "",
                          actualBehavior: "",
                          attachments: [],
                          systemInfo: formData.systemInfo
                        });
                        setFormErrors({});
                      }}
                      startIcon={<Clear />}
                    >
                      Réinitialiser
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                      startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
                      sx={{ borderRadius: 2 }}
                    >
                      {submitting ? "Envoi en cours..." : "Envoyer le ticket"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Fade>
      ) : (
        <Fade in>
          <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    placeholder="Rechercher un ticket..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Assignment fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Statut"
                    >
                      <MenuItem value="all">Tous</MenuItem>
                      {statuses.map(s => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Priorité</InputLabel>
                    <Select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      label="Priorité"
                    >
                      <MenuItem value="all">Toutes</MenuItem>
                      {priorities.map(p => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={loadTickets}
                    startIcon={<Refresh />}
                  >
                    Actualiser
                  </Button>
                </Grid>
              </Grid>
            </Paper>
            
            {/* Tickets List */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredTickets.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
                <History sx={{ fontSize: 60, color: "#9ca3af", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Aucun ticket trouvé
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || filterStatus !== "all" || filterPriority !== "all" 
                    ? "Essayez de modifier vos filtres" 
                    : "Créez votre premier ticket pour obtenir du support"}
                </Typography>
              </Paper>
            ) : (
              <>
                <Grid container spacing={2}>
                  {paginatedTickets.map((ticket) => {
                    const priorityInfo = getPriorityInfo(ticket.priority);
                    const statusInfo = getStatusInfo(ticket.status);
                    const categoryInfo = getCategoryInfo(ticket.category);
                    
                    return (
                      <Grid item xs={12} key={ticket.id}>
                        <Card 
                          sx={{ 
                            borderRadius: 2,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 8px 16px rgba(0,0,0,0.1)"
                            },
                            cursor: "pointer"
                          }}
                          onClick={() => viewTicketDetails(ticket)}
                        >
                          <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  #{ticket.id?.slice(-6)}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {ticket.title}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Chip
                                  icon={priorityInfo.icon}
                                  label={priorityInfo.label}
                                  size="small"
                                  sx={{ bgcolor: `${priorityInfo.color}20`, color: priorityInfo.color }}
                                />
                                <Chip
                                  icon={statusInfo.icon}
                                  label={statusInfo.label}
                                  size="small"
                                  sx={{ bgcolor: `${statusInfo.color}20`, color: statusInfo.color }}
                                />
                              </Box>
                            </Box>
                            
                            <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
                              <Chip
                                icon={categoryInfo.icon}
                                label={categoryInfo.label}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                icon={<AccessTime />}
                                label={formatDate(ticket.createdAt)}
                                size="small"
                                variant="outlined"
                              />
                              {ticket.updatedAt && ticket.updatedAt !== ticket.createdAt && (
                                <Chip
                                  icon={<Update />}
                                  label={`Mis à jour: ${formatDate(ticket.updatedAt)}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ mt: 2, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                            >
                              {ticket.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                
                {filteredTickets.length > rowsPerPage && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                      count={Math.ceil(filteredTickets.length / rowsPerPage)}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      color="primary"
                      size="large"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Fade>
      )}
      
      {/* Ticket Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={Grow}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedTicket && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Assignment color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    #{selectedTicket.id?.slice(-6)} - {selectedTicket.title}
                  </Typography>
                </Box>
                <IconButton onClick={closeDialog}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    <Chip
                      icon={getPriorityInfo(selectedTicket.priority).icon}
                      label={getPriorityInfo(selectedTicket.priority).label}
                      sx={{ bgcolor: `${getPriorityInfo(selectedTicket.priority).color}20`, color: getPriorityInfo(selectedTicket.priority).color }}
                    />
                    <Chip
                      icon={getStatusInfo(selectedTicket.status).icon}
                      label={getStatusInfo(selectedTicket.status).label}
                      sx={{ bgcolor: `${getStatusInfo(selectedTicket.status).color}20`, color: getStatusInfo(selectedTicket.status).color }}
                    />
                    <Chip
                      icon={getCategoryInfo(selectedTicket.category).icon}
                      label={getCategoryInfo(selectedTicket.category).label}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Créé par</Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Person fontSize="small" />
                    {selectedTicket.userName || selectedTicket.userEmail}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Date de création</Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarToday fontSize="small" />
                    {formatDate(selectedTicket.createdAt)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {selectedTicket.description}
                  </Typography>
                </Grid>
                
                {selectedTicket.stepsToReproduce && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Étapes pour reproduire
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fafc" }}>
                      <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                        {selectedTicket.stepsToReproduce}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                
                {selectedTicket.expectedBehavior && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Comportement attendu
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fafc" }}>
                      <Typography variant="body2">
                        {selectedTicket.expectedBehavior}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                
                {selectedTicket.actualBehavior && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Comportement actuel
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fef2f2" }}>
                      <Typography variant="body2" color="error">
                        {selectedTicket.actualBehavior}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                
                {selectedTicket.systemInfo && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Informations système
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fafc" }}>
                      <Typography variant="caption" display="block">
                        <strong>Navigateur:</strong> {selectedTicket.systemInfo.browser?.substring(0, 150)}...
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>OS:</strong> {selectedTicket.systemInfo.os}
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>Résolution:</strong> {selectedTicket.systemInfo.screenResolution}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>Fermer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      <StyledSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default ReclamationPage;